import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PinGenerator from './components/PinGenerator';
import VideoCreator from './components/VideoCreator';
import InfoVideoCreator from './components/InfoVideoCreator';
import VideoCreatorV2 from './components/VideoCreatorV2';
import InfoVideoCreatorV2 from './components/InfoVideoCreatorV2';
import JsonScriptLoader from './components/JsonScriptLoader';
import BlogGenerator from './components/BlogGenerator.tsx';
import ContentOptionsGenerator from './components/ContentOptionsGenerator';
import ProductImageGenerator from './components/ProductImageGenerator';
import LeadMagnetMaker from './components/LeadMagnetMaker';
import ProductRetrieval from './components/ProductRetrieval';
import VideoPromptGenerator from './components/VideoPromptGenerator';
import TrendLab from './src/components/TrendLab';
import ImageCompressor from './components/ImageCompressor';
import PostingCalendar from './src/components/PostingCalendar';
import Login from './src/components/Login';
import Account from './src/components/Account';
import Settings from './components/Settings';
import InteriorDesignAgent from './src/components/InteriorDesignAgent';
import ProductSync from './src/components/ProductSync';
import PrivacyPolicy from './src/components/PrivacyPolicy';
import TermsOfService from './src/components/TermsOfService';
import { API_BASE } from './apiBase';
import { getSessionToken, getShopifyParams, isEmbedded, installAuthenticatedFetch } from './src/shopifyAppBridge';
import './index.css';

installAuthenticatedFetch();

// Configure axios to use session tokens for Shopify embedded app authentication
axios.interceptors.request.use(async (config) => {
  // Try to get session token from App Bridge (for embedded apps)
  if (isEmbedded()) {
    try {
      const sessionToken = await getSessionToken();
      if (sessionToken) {
        config.headers['Authorization'] = `Bearer ${sessionToken}`;
        config.headers['X-Shopify-Session-Token'] = sessionToken;
        console.log('🔐 Using session token for request');
      }
    } catch (error) {
      console.warn('Failed to get session token:', error);
    }
  }

  // Fallback: Include session ID header for cross-origin auth
  const sessionId = localStorage.getItem('sessionId');
  if (sessionId) {
    config.headers['X-Session-ID'] = sessionId;
  }

  // Include shop parameter if available
  const { shop } = getShopifyParams();
  if (shop) {
    config.headers['X-Shopify-Shop'] = shop;
  }

  return config;
});

// Clear session ID on 401 responses (session expired)
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('sessionId');
    }
    return Promise.reject(error);
  }
);

type ActiveTool = 'pins' | 'videos' | 'video-prompts' | 'blog' | 'content-options' | 'product-images' | 'lead-magnet' | 'product-retrieval' | 'trend-lab' | 'calendar' | 'account' | 'settings' | 'image-compressor' | 'interior-agent' | 'product-sync';
type VideoType = 'ecom' | 'info';
type VideoVersion = 'v1' | 'v2';

interface NavItem {
  id: ActiveTool;
  label: string;
  icon: string;
}

const navItems: NavItem[] = [
  { id: 'blog', label: 'Blog Generator', icon: '📝' },
  // { id: 'pins', label: 'Social Media Content Creator', icon: '�' }, // Coming Soon
  { id: 'product-images', label: 'Product Images', icon: '📸' },
  { id: 'videos', label: 'Video Creator', icon: '🎬' },
  { id: 'lead-magnet', label: 'Lead Magnet', icon: '📚' },
  // { id: 'content-options', label: 'Content Options', icon: '🎨' },
  { id: 'product-retrieval', label: 'Product Retrieval', icon: '🛍️' },
  { id: 'trend-lab', label: 'Trend Lab', icon: '📊' },
  { id: 'image-compressor', label: 'Image Compressor', icon: '🗜️' },
  { id: 'calendar', label: 'Calendar', icon: '📅' },
  // { id: 'interior-agent', label: 'Interior Agent', icon: '🕵️‍♀️' }, // Coming Soon
  { id: 'product-sync', label: 'Product Sync', icon: '🔄' },
  { id: 'settings', label: 'Settings', icon: '⚙️' },
  { id: 'account', label: 'Account', icon: '👤' },
];

const NavButton = ({ isActive, onClick, children }: { isActive: boolean, onClick: () => void, children: React.ReactNode }) => (
  <button
    onClick={onClick}
    className={`px-3 py-2 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-semibold transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500 whitespace-nowrap ${isActive
      ? 'bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white shadow-md'
      : 'bg-white text-slate-600 hover:bg-slate-100'
      }`}
  >
    {children}
  </button>
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const [activeTool, setActiveTool] = useState<ActiveTool>('blog');
  const [videoType, setVideoType] = useState<VideoType>('ecom');
  const [videoVersion, setVideoVersion] = useState<VideoVersion>('v1');
  const [appToast, setAppToast] = React.useState<string | null>(null);

  // Check authentication status on mount
  useEffect(() => {
    const isLocalDevelopment = window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1';

    if (isLocalDevelopment) {
      // For local development, skip auth check and assume authenticated
      console.log('🔓 Auto-authenticating for local development (skipping API check)');
      setIsAuthenticated(true);
      return;
    }

    // Check if we're in Shopify embedded context first
    const checkEmbeddedAuth = async () => {
      if (isEmbedded()) {
        console.log('🛒 Detected Shopify embedded context, attempting session token auth...');

        try {
          // Try to get a session token - if successful, consider authenticated
          const sessionToken = await getSessionToken();

          if (sessionToken) {
            console.log('✅ Session token obtained, verifying with backend...');

            // Verify the session token with the backend
            const response = await axios.get(`${API_BASE}/api/auth/check`, {
              withCredentials: true,
              headers: {
                'Authorization': `Bearer ${sessionToken}`,
                'X-Shopify-Session-Token': sessionToken
              }
            });

            if (response.data.authenticated) {
              console.log('✅ Embedded app authenticated via session token');
              setIsAuthenticated(true);
              return;
            }
          }

          // If we're in embedded context but session token verification failed,
          // still try to authenticate - the backend middleware might have already
          // set up the session from the token
          console.log('⚠️ Session token verification returned false, trying standard auth check...');
        } catch (error) {
          console.error('❌ Embedded auth error:', error);
        }
      }

      // A Shopify-embedded app must never fall through to the unrelated Catalog
      // Muse login. App Bridge supplies the user identity on every request.
      if (isEmbedded()) {
        setIsAuthenticated(false);
        return;
      }

      checkAuth();
    };

    checkEmbeddedAuth();
  }, []);

  // Check if we're on a public page (privacy policy, terms of service)
  const pathname = window.location.pathname;
  if (pathname === '/privacy-policy' || pathname === '/privacy') {
    return <PrivacyPolicy />;
  }
  if (pathname === '/terms-of-service' || pathname === '/terms') {
    return <TermsOfService />;
  }


  // Allow children to request opening the V1 recorder via a window event
  useEffect(() => {
    if (!isAuthenticated) return;

    const handler = (e: Event) => {
      try { console.log('[App] open-recorder-v1 event received'); } catch { }
      setActiveTool('videos');
      setVideoType('ecom');
      setVideoVersion('v1');
      setAppToast('Opening V1 recorder…');
      setTimeout(() => setAppToast(null), 2500);
      try { document.querySelector('main')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch { }
    };
    window.addEventListener('open-recorder-v1', handler as any);
    return () => window.removeEventListener('open-recorder-v1', handler as any);
  }, [isAuthenticated]);

  const checkAuth = async () => {
    console.log('🚀 [App.tsx] Initiating auth check with API_BASE:', API_BASE);
    try {
      const response = await axios.get(`${API_BASE}/api/auth/check`, { withCredentials: true });
      setIsAuthenticated(response.data.authenticated);
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
    }
  };

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API_BASE}/api/auth/logout`, {}, { withCredentials: true });
      localStorage.removeItem('sessionId'); // Clear session ID on logout
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout failed:', error);
      localStorage.removeItem('sessionId'); // Clear anyway
    }
  };

  // Show loading while checking auth
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500 mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const handleNavClick = (tool: ActiveTool) => {
    setActiveTool(tool);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto p-4 sm:p-8">
        <header className="text-center mb-6 sm:mb-10">
          <div className="flex justify-between items-center mb-4">
            <div className="flex-1"></div>

            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight bg-gradient-to-r from-fuchsia-500 to-pink-500 text-transparent bg-clip-text">
                Catalog Muse
              </h1>
            </div>

            <div className="flex-1 flex justify-end">
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
              >
                🚪 Logout
              </button>
            </div>
          </div>
          <p className="mt-4 text-base sm:text-lg text-slate-600 max-w-2xl mx-auto">
            Your all-in-one tool to generate stunning visuals for pins, videos, and blog posts.
          </p>
        </header>

        {/* Navigation Slider */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg shadow-sm border border-slate-200 overflow-x-auto max-w-full">
            {navItems.map((item) => (
              <NavButton
                key={item.id}
                isActive={activeTool === item.id}
                onClick={() => handleNavClick(item.id)}
              >
                <span className="hidden xl:inline">{item.icon} </span>
                {item.label}
              </NavButton>
            ))}
          </div>
        </div>

        {activeTool === 'pins' && <PinGenerator />}
        {activeTool === 'blog' && <BlogGenerator />}
        {activeTool === 'video-prompts' && <VideoPromptGenerator />}
        {activeTool === 'content-options' && <ContentOptionsGenerator />}
        {activeTool === 'product-images' && <ProductImageGenerator />}
        {activeTool === 'lead-magnet' && <LeadMagnetMaker />}
        {activeTool === 'product-retrieval' && <ProductRetrieval />}
        {activeTool === 'trend-lab' && <TrendLab />}
        {activeTool === 'image-compressor' && <ImageCompressor />}
        {activeTool === 'calendar' && <PostingCalendar />}
        {activeTool === 'interior-agent' && <InteriorDesignAgent />}
        {activeTool === 'product-sync' && <ProductSync />}
        {activeTool === 'settings' && <Settings />}
        {activeTool === 'account' && <Account />}
        {activeTool === 'videos' && (
          <div className="max-w-4xl mx-auto p-6 space-y-6 relative">
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">🎬 Video Creator</h1>
              <p className="text-slate-600">
                Create product showcases and educational videos with AI-generated scripts and images.
              </p>
            </div>

            {/* Coming Soon Overlay */}
            <div className="absolute inset-0 bg-white/50 backdrop-blur-[2px] z-20 flex items-center justify-center cursor-not-allowed">
              <div className="bg-white border border-slate-200 p-8 rounded-xl shadow-2xl text-center max-w-md transform scale-110">
                <div className="text-5xl mb-4">🚧</div>
                <h2 className="text-3xl font-bold bg-gradient-to-r from-fuchsia-600 to-pink-600 bg-clip-text text-transparent mb-2">
                  Coming Soon
                </h2>
                <p className="text-slate-600 text-lg mb-6">
                  Our Video Creator is being rebuilt with a better workflow, AI image generation, and drag-and-drop editing.
                </p>
                <button disabled className="px-6 py-3 bg-fuchsia-100 text-fuchsia-600 font-semibold rounded-full cursor-not-allowed">
                  Notify Me When Ready
                </button>
              </div>
            </div>

            {/* Blurred preview content */}
            <div className="relative opacity-40 pointer-events-none select-none filter blur-sm">
              <div className="flex justify-center mb-6">
                <div className="flex items-center gap-2 bg-white p-1.5 rounded-lg shadow-sm border border-slate-200">
                  <div className="px-4 py-2 rounded-md text-sm font-semibold bg-gradient-to-r from-fuchsia-600 to-pink-600 text-white shadow-md">
                    🛍️ Ecom Video
                  </div>
                  <div className="px-4 py-2 rounded-md text-sm font-semibold bg-white text-slate-600">
                    📚 Info Video
                  </div>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
                <h3 className="text-lg font-semibold text-slate-800 mb-4">Create Video Script</h3>
                <div className="space-y-4">
                  <div className="h-24 bg-slate-100 rounded-lg"></div>
                  <div className="flex gap-2">
                    <div className="h-10 bg-slate-200 rounded-lg flex-1"></div>
                    <div className="h-10 bg-slate-200 rounded-lg w-32"></div>
                  </div>
                  <div className="h-12 bg-gradient-to-r from-fuchsia-200 to-pink-200 rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        )}

      </main>
      {appToast && (
        <div className="fixed bottom-4 right-4 px-4 py-2 rounded-lg bg-slate-900 text-white shadow-lg">{appToast}</div>
      )}

      {/* Footer with legal links */}
      <footer className="bg-slate-100 border-t border-slate-200 py-4 px-6 mt-auto">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2 text-sm text-slate-500">
          <p>© 2025 Catalog Muse by Amin Boulabaim Amjahid</p>
          <div className="flex gap-4">
            <a href="/privacy-policy" className="hover:text-slate-700 transition-colors">Privacy Policy</a>
            <span>•</span>
            <a href="/terms-of-service" className="hover:text-slate-700 transition-colors">Terms of Service</a>
            <span>•</span>
            <a href="mailto:amin@innerunionhome.com" className="hover:text-slate-700 transition-colors">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
